import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import qs from 'query-string';

import { DASHBOARD_PATHS } from '@ratehub/dashboards';
import { LANGUAGES } from '@ratehub/documents/common/stable';
import {
    useSession,
    SignInModal,
    fetchActiveLogins,
    fetchHasMortgageApplications,
    getDashboardBasePath,
    requestMagicLink,
    MAGIC_LINK_SIGN_IN_SCREEN,
    MAGIC_LINK_CONFIRMATION_SCREEN,

    BouncingDecimal,
    Colours,
    Sizes,
    Paragraph,
    PartnerBannerCanwise,
    navigateWindowTo,
    trackHeapEvent,
    getLanguageFromLocale,
    noticeError,
    Config,
} from '@ratehub/base-ui';

import classNames from 'classnames';
import Layout from './Layout';


function MtgLandingPage() {
    // Because messaging depends on query strings, have to hide them until client-side

    const sessionStore = useSession();

    const [ isFirstRender, setIsFirstRender ] = useState(true);

    const [ isRequestingEmail, setIsRequestingEmail ] = useState(false);
    const [ errorMessageKey, setErrorMessageKey ] = useState(null);

    const { applicationID, applicationPage, agent, scenario, email } = qs.parse(isFirstRender ? '' : location.search);

    const isCanwiseFlow = (isFirstRender ? '' : location.pathname).split('/').pop() === 'canwise';
    const supportPhone = isCanwiseFlow ? Config.CANWISE_SUPPORT_PHONE : Config.RATEHUB_SUPPORT_PHONE;

    const intl = useIntl();
    const lang = getLanguageFromLocale(intl.locale);

    async function checkForActiveSessions() {
        let hasRatehubLogin;
        try {
            ({ hasRatehubLogin } = await fetchActiveLogins());
        } catch (e) {
            raiseError(e, 'other', 'Unknown exception occurred while fetching active login sessions');
        }

        if (hasRatehubLogin) {
            // redirect to application
            navigateWindowTo(
                getAppCaURL(
                    applicationID ? applicationPage : 'list',
                    intl.locale,
                    {
                        email: sessionStore.email,
                        leadID: applicationID,
                        agent,
                        fromLanding: true,
                    },
                ),
            );
        } else {
            setIsRequestingEmail(true);
        }
    }

    async function handleRequestMagicOrRedirect(email) {
        const checkForAccountsResult = await checkForAccounts(email);
        if (checkForAccountsResult.hasAppCa) {
            await requestMagicLink(email, { agent, lang }, {}, 'agentlink');
        } else {
            setIsRequestingEmail(false);
            // redirect to application
            navigateWindowTo(getAppCaURL('new', intl.locale, { email, agent, scenario }));
        }
    }

    async function checkForAccounts(email) {
        let hasAppCa, newestAppCaCreated;
        try {
            ({ hasAppCa, newestAppCaCreated } = await fetchHasMortgageApplications(
                email,
            ));
        } catch (e) {
            raiseError(e, 'other', 'Unknown exception occurred while fetching user mortgage applications');
        }

        return { hasAppCa, newestAppCaCreated };
    }

    function handleClickLanguage() {
        const queries = qs.parse(location.search);
        queries.lang = intl.locale.slice(0, 2) === LANGUAGES.ENGLISH ? LANGUAGES.FRENCH : LANGUAGES.ENGLISH;

        navigateWindowTo(`${location.pathname}?${qs.stringify(queries)}`);
    }

    useEffect(() => {
        setIsFirstRender(false);
    }, []);

    useEffect(() => {
        if (sessionStore.hasFetchedSession) {
            checkForActiveSessions();
        }
    }, [ sessionStore.hasFetchedSession ]);

    function raiseError(originalError, errorKey, errorMessage) {
        noticeError(originalError, { message: errorMessage });

        trackHeapEvent('Error message triggered on mortgage landing page', { errorKey, errorMessage, originalError });

        setErrorMessageKey(errorKey);
    }

    return (
        <Layout
            hideBackgroundImage
            disableAccounts={true}
            isGridDisabled
        >
            <If condition={!isFirstRender}>
                <PartnerBannerCanwise onClickLanguage={handleClickLanguage} />
            </If>
            <MtgLandingPage.Overlay>
                <Choose>
                    <When condition={errorMessageKey}>
                        <ErrorContent>
                            <h1 className={classNames('rh-title-m', 'rh-mt-3', 'rh-mr-0', 'rh-mb-0', 'showDot')}>
                                <FormattedMessage
                                    id="MortgageLandingPage.ErrorTitle"
                                    defaultMessage="{errorMessageKey, select,
                                            other {Sorry, something went wrong}
                                        }"
                                    values={{ errorMessageKey }}
                                />
                            </h1>
                            <Paragraph
                                as="h2"
                                size="large"
                                message={(
                                    <FormattedMessage
                                        id="MortgageLandingPage.ErrorSubtitle"
                                        defaultMessage="{errorMessageKey, select,
                                            other {Please try logging out and clearing your cache and try again.}
                                        }"
                                        values={{ errorMessageKey }}
                                    />
                                )}
                                className="errorSubtitle"
                            />
                            <Paragraph
                                size="small"
                                message={(
                                    <FormattedMessage
                                        id="MortgageLandingPage.ErrorSupportMessage"
                                        defaultMessage="If you are experiencing any issues, please call us at {supportPhone}."
                                        values={{ supportPhone }}
                                    />
                                )}
                            />
                        </ErrorContent>
                    </When>
                    <When condition={isRequestingEmail}>
                        <SignInModal
                            isOpen={!!isRequestingEmail}
                            onClose={() => {
                                document.location.href = '/';
                            }}
                            partnerBranding={<PartnerBannerCanwise onClickLanguage={handleClickLanguage} />}
                            supportPhone={supportPhone}
                            messages={{
                                [MAGIC_LINK_SIGN_IN_SCREEN]: {
                                    MAGIC_LINK_SIGN_IN_TITLE: MESSAGES.MAGIC_LINK_SIGN_IN_TITLE,
                                },
                                [MAGIC_LINK_CONFIRMATION_SCREEN]: {
                                    MAGIC_LINK_CONFIRMATION_TITLE: MESSAGES.MAGIC_LINK_CONFIRMATION_TITLE,
                                    MAGIC_LINK_CONFIRMATION_DESCRIPTION: MESSAGES.MAGIC_LINK_CONFIRMATION_DESCRIPTION,
                                },
                            }}
                            onRequestLinkCustom={async email => await handleRequestMagicOrRedirect(email)}
                            emailForForcedLogin={typeof isRequestingEmail === 'string' ? isRequestingEmail : email}
                            hideLoginDescription
                        />
                    </When>
                    <Otherwise>
                        <If condition={!isFirstRender}>
                            <BouncingDecimal />
                            <h1 className={classNames('rh-title-m', 'showDot')}>
                                <Choose>
                                    <When condition={applicationID}>
                                        <FormattedMessage
                                            id="MortgageLandingPage.SpinnerTextApplication"
                                            defaultMessage="We're retrieving your application"
                                        />
                                    </When>
                                    <Otherwise>
                                        <FormattedMessage
                                            id="MortgageLandingPage.SpinnerTextDefault"
                                            defaultMessage="You are being directed to the appropriate page"
                                        />
                                    </Otherwise>
                                </Choose>
                            </h1>
                        </If>
                    </Otherwise>
                </Choose>
            </MtgLandingPage.Overlay>
        </Layout>
    );
}

MtgLandingPage.Overlay = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    max-width: 620px;
    margin: 15vh auto 0;
    padding: ${Sizes.SPACING.TWO};

    text-align: center;
    background-color: ${Colours.COCONUT};

    > .errorSubtitle {
        margin-bottom: ${Sizes.SPACING.TWO};
    }
`;

const ErrorContent = styled.div`
    text-align: left;
`;

function getAppCaURL(page, locale, queryParams) {
    const hasScenario = !!queryParams.scenario;
    if (page === 'new' && !hasScenario) {
        queryParams.fromLanding = true;
    }

    const queries = qs.stringify(queryParams);
    return `${getAppCaPath(page, locale)}${queries ? `?${queries}` : ''}`;
}

function getAppCaPath(page, locale) {
    const lang = locale.slice(0, 2);
    const mortgagesPath = lang === 'fr' ? '/hypotheque' : '/mortgages';
    const appCaPath = `${mortgagesPath}/${lang === 'fr' ? 'appliquez' : 'apply'}`;
    switch (page) {
        case 'list':
            return `${getDashboardBasePath(locale)}${DASHBOARD_PATHS.myQuotes.fullRoute}`;
        case 'booking':
            return `${appCaPath}/appointment-booking`;
        case 'upload':
            return `${appCaPath}/document-upload`;
        case 'new': // should go to app.ca mortgage type page
        default:
            return appCaPath;
    }
}

const MESSAGES = defineMessages({
    MAGIC_LINK_SIGN_IN_TITLE: {
        id: 'MortgageLandingPage.MagicLinkTitle',
        defaultMessage: 'Please enter your email address so we can determine if you have an existing application',
    },
    MAGIC_LINK_CONFIRMATION_TITLE: {
        id: 'MortgageLandingPage.MagicLinkConfirmationTitle',
        defaultMessage: 'A secure link has been sent to your inbox',
    },
    MAGIC_LINK_CONFIRMATION_DESCRIPTION: {
        id: 'MortgageLandingPage.MagicLinkConfirmationDescription',
        defaultMessage: 'Please check your inbox and click on the link to access your account.',
    },
});

export default observer(MtgLandingPage);
